/**
 * 저장 확인 기록 `ccatlas.save-confirmation/1` (TASK-2026-003 CR-01 §3.1).
 *
 * 저장 호출이 **예외 없이 끝났다**는 사실과 저장이 **확인됐다**는 사실은
 * 다르다(TASK-2026-002 REV-029·REV-031). 이 모듈은 확인한 결과만 구조화된
 * 기록으로 만든다.
 *
 * 규칙을 주석이 아니라 검사기로 강제한다.
 * - 필드는 아래 목록뿐이다. 원문 저장값·설문 원문·초대코드·cookie/token·
 *   관리자 정보·IP/UA를 담을 자리가 없고, 목록 밖 필드는 거부한다.
 * - `verified_by`는 **실제로 확인한 방법**이다. 브라우저 되읽기는 그 순간
 *   같은 값이 읽혔다는 뜻이고, Firestore commit은 서비스가 commit을 돌려줬다는
 *   뜻이다. 디스크 fsync·복제·이후 보존은 주장하지 않는다.
 * - `confirmed`에는 사유가 없고 `failed`에는 제한된 사유 코드가 반드시 있다.
 *
 * **비재귀 계약**은 `createConfirmationEmitter`가 맡는다. 확인 기록은 저장소에
 * 쓰지 않으므로 확인 기록을 낳지 않고, 관찰자에게 알리는 도중에 생긴 기록은
 * 버리고 수만 센다.
 */

import { CONTENT_HASH_PATTERN } from './canonical';

export const CONFIRMATION_SCHEMA = 'ccatlas.save-confirmation/1';

export const VERIFIED_BY = [
  'browser_storage_readback',
  'firestore_transaction_commit',
  'firestore_commit_readback',
] as const;
export type VerifiedBy = (typeof VERIFIED_BY)[number];

export const BROWSER_OPERATIONS = [
  'telemetry_events',
  'telemetry_counter',
  'telemetry_restore',
  'telemetry_clear',
] as const;
export const SERVER_OPERATIONS = ['session_create', 'event_batch', 'response', 'complete', 'withdraw'] as const;
export type ConfirmationOperation =
  | (typeof BROWSER_OPERATIONS)[number]
  | (typeof SERVER_OPERATIONS)[number];

export const FAILURE_REASONS = [
  'threw',
  'readback_mismatch',
  'readback_failed',
  'original_unreadable',
  'storage_unavailable',
  'conflict',
  'data_missing',
  'hash_mismatch',
] as const;
export type FailureReason = (typeof FAILURE_REASONS)[number];

export const STORAGE_KEY_NAMES = ['events', 'dropped', 'rejected', 'invalid'] as const;
export type StorageKeyName = (typeof STORAGE_KEY_NAMES)[number];

export interface ConfirmationKey {
  readonly storage_key?: StorageKeyName;
  readonly batch_id?: string;
  readonly first_seq?: number;
  readonly last_seq?: number;
  readonly response_version?: number;
  readonly through_seq?: number;
}

export interface SaveConfirmation {
  readonly schema: typeof CONFIRMATION_SCHEMA;
  readonly verified_by: VerifiedBy;
  readonly operation: ConfirmationOperation;
  readonly session_ref: string | null;
  readonly attempt_ref: string | null;
  readonly key: ConfirmationKey;
  readonly result: 'confirmed' | 'failed';
  readonly reason: FailureReason | null;
  readonly checked_at: number;
  readonly content_hash: string | null;
  readonly ack: { readonly through_seq: number } | null;
}

/** 서버가 발급하는 비식별 세션 참조. 쿠키 토큰과 다른 값이다. */
export const SESSION_REF_PATTERN = /^s[0-9a-z]{16,40}$/;
/** `events.ts`의 `attempt_id`와 같은 모양. */
export const ATTEMPT_REF_PATTERN = /^a[0-9a-z]{4,40}$/;
/** 클라이언트가 만드는 batch ID. */
export const BATCH_ID_PATTERN = /^b[0-9a-z]{8,40}$/;

const TOP_LEVEL_FIELDS: readonly string[] = Object.freeze([
  'schema',
  'verified_by',
  'operation',
  'session_ref',
  'attempt_ref',
  'key',
  'result',
  'reason',
  'checked_at',
  'content_hash',
  'ack',
]);

const KEY_FIELDS: readonly string[] = Object.freeze([
  'storage_key',
  'batch_id',
  'first_seq',
  'last_seq',
  'response_version',
  'through_seq',
]);

function includes(list: readonly string[], value: unknown): boolean {
  return typeof value === 'string' && list.includes(value);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isCount(value: unknown, min: number): boolean {
  return typeof value === 'number' && Number.isSafeInteger(value) && value >= min;
}

/** 확인 기록 하나를 검사한다. 위반을 모두 돌려주며 빈 배열이면 유효하다. */
export function validateConfirmation(value: unknown): readonly string[] {
  if (!isRecord(value)) {
    return ['확인 기록은 객체여야 합니다.'];
  }
  const errors: string[] = [];

  for (const field of Object.keys(value)) {
    if (!TOP_LEVEL_FIELDS.includes(field)) {
      errors.push(`허용되지 않은 필드 '${field}'`);
    }
  }
  for (const field of TOP_LEVEL_FIELDS) {
    if (!(field in value)) {
      errors.push(`필드 '${field}'가 없습니다.`);
    }
  }

  if (value['schema'] !== CONFIRMATION_SCHEMA) {
    errors.push('schema가 다릅니다.');
  }
  const verifiedBy = value['verified_by'];
  const operation = value['operation'];
  if (!includes(VERIFIED_BY, verifiedBy)) {
    errors.push('verified_by가 허용된 값이 아닙니다.');
  }
  const browserOperation = includes(BROWSER_OPERATIONS, operation);
  const serverOperation = includes(SERVER_OPERATIONS, operation);
  if (!browserOperation && !serverOperation) {
    errors.push('operation이 허용된 값이 아닙니다.');
  } else if (verifiedBy === 'browser_storage_readback' ? !browserOperation : !serverOperation) {
    // 브라우저 되읽기로 서버 저장을 확인했다고 말할 수 없고, 그 반대도 같다.
    errors.push('verified_by와 operation이 같은 저장소를 가리키지 않습니다.');
  }

  const sessionRef = value['session_ref'];
  if (sessionRef !== null && !(typeof sessionRef === 'string' && SESSION_REF_PATTERN.test(sessionRef))) {
    errors.push('session_ref 형식이 아닙니다.');
  }
  const attemptRef = value['attempt_ref'];
  if (attemptRef !== null && !(typeof attemptRef === 'string' && ATTEMPT_REF_PATTERN.test(attemptRef))) {
    errors.push('attempt_ref 형식이 아닙니다.');
  }

  const key = value['key'];
  if (!isRecord(key)) {
    errors.push('key는 객체여야 합니다.');
  } else {
    for (const [field, item] of Object.entries(key)) {
      if (!KEY_FIELDS.includes(field)) {
        errors.push(`key에 허용되지 않은 필드 '${field}'`);
        continue;
      }
      if (field === 'storage_key') {
        if (!includes(STORAGE_KEY_NAMES, item)) {
          errors.push('key.storage_key가 허용된 값이 아닙니다.');
        }
      } else if (field === 'batch_id') {
        if (!(typeof item === 'string' && BATCH_ID_PATTERN.test(item))) {
          errors.push('key.batch_id 형식이 아닙니다.');
        }
      } else if (!isCount(item, field === 'through_seq' ? 0 : 1)) {
        errors.push(`key.${field}는 범위 안의 정수여야 합니다.`);
      }
    }
  }

  const result = value['result'];
  const reason = value['reason'];
  if (result === 'confirmed') {
    if (reason !== null) {
      errors.push('confirmed에는 사유가 없어야 합니다.');
    }
  } else if (result === 'failed') {
    if (!includes(FAILURE_REASONS, reason)) {
      errors.push('failed에는 허용된 사유 코드가 있어야 합니다.');
    }
  } else {
    errors.push('result는 confirmed 또는 failed여야 합니다.');
  }

  if (!isCount(value['checked_at'], 0)) {
    errors.push('checked_at은 0 이상의 정수(epoch ms)여야 합니다.');
  }
  const hash = value['content_hash'];
  if (hash !== null && !(typeof hash === 'string' && CONTENT_HASH_PATTERN.test(hash))) {
    errors.push('content_hash 형식이 아닙니다.');
  }
  const ack = value['ack'];
  if (ack !== null) {
    if (!isRecord(ack) || Object.keys(ack).length !== 1 || !isCount(ack['through_seq'], 0)) {
      errors.push('ack는 {through_seq} 하나만 담아야 합니다.');
    }
  }
  return errors;
}

export interface ConfirmationInput {
  readonly verifiedBy: VerifiedBy;
  readonly operation: ConfirmationOperation;
  readonly sessionRef?: string | null;
  readonly attemptRef?: string | null;
  readonly key?: ConfirmationKey;
  readonly result: 'confirmed' | 'failed';
  readonly reason?: FailureReason | null;
  readonly checkedAt: number;
  readonly contentHash?: string | null;
  readonly ackThroughSeq?: number | null;
}

/** 검사를 통과한 확인 기록을 만든다. 위반이면 던진다. */
export function createConfirmation(input: ConfirmationInput): SaveConfirmation {
  const record: SaveConfirmation = {
    schema: CONFIRMATION_SCHEMA,
    verified_by: input.verifiedBy,
    operation: input.operation,
    session_ref: input.sessionRef ?? null,
    attempt_ref: input.attemptRef ?? null,
    key: Object.freeze({ ...(input.key ?? {}) }),
    result: input.result,
    reason: input.result === 'confirmed' ? null : (input.reason ?? null),
    checked_at: input.checkedAt,
    content_hash: input.contentHash ?? null,
    ack:
      input.ackThroughSeq === undefined || input.ackThroughSeq === null
        ? null
        : Object.freeze({ through_seq: input.ackThroughSeq }),
  };
  const errors = validateConfirmation(record);
  if (errors.length > 0) {
    throw new Error(`저장 확인 기록이 schema를 위반했습니다: ${errors.join(' / ')}`);
  }
  return Object.freeze(record);
}

export type ConfirmationSink = (record: SaveConfirmation) => void;

export interface ConfirmationEmitterStats {
  /** 관찰자에게 알리는 도중 생겨 버린 기록 수(비재귀 계약). */
  readonly suppressed: number;
  /** 관찰자가 던진 횟수. 저장 판정에는 영향이 없다. */
  readonly observerErrors: number;
  /** 만들다 schema를 위반해 버린 기록 수. */
  readonly invalid: number;
}

export interface ConfirmationEmitter {
  /** 관찰자가 있는가. 없으면 기록을 만들지 않는다(hash 비용도 들지 않는다). */
  readonly enabled: boolean;
  /** 작업 도중 확인 결과를 모은다. */
  collect(build: () => ConfirmationInput): void;
  /** 작업이 끝났을 때 모은 기록을 관찰자에게 넘긴다. */
  flush(): void;
  stats(): ConfirmationEmitterStats;
}

/**
 * 저장 확인 기록을 모았다가 **작업이 끝난 뒤** 관찰자에게 넘긴다.
 *
 * 작업 도중에 알리면 관찰자가 그 자리에서 다시 기록해 진행 중인 저장을 흔들 수
 * 있다. 그래서 모았다가 한 번에 넘긴다. 넘기는 동안 관찰자가 일으킨 저장의
 * 확인 기록은 **버리고 센다** —— 그러지 않으면 기록이 기록을 낳는 순환이 된다.
 * 관찰자 예외는 삼키고 센다. 저장 판정은 관찰자와 무관해야 한다.
 */
export function createConfirmationEmitter(sink?: ConfirmationSink): ConfirmationEmitter {
  let pending: SaveConfirmation[] = [];
  let notifying = false;
  let suppressed = 0;
  let observerErrors = 0;
  let invalid = 0;

  return {
    enabled: sink !== undefined,
    collect(build) {
      if (sink === undefined) {
        return;
      }
      if (notifying) {
        suppressed += 1;
        return;
      }
      try {
        pending.push(createConfirmation(build()));
      } catch {
        invalid += 1;
      }
    },
    flush() {
      if (sink === undefined || notifying || pending.length === 0) {
        return;
      }
      const batch = pending;
      pending = [];
      notifying = true;
      try {
        for (const record of batch) {
          try {
            sink(record);
          } catch {
            observerErrors += 1;
          }
        }
      } finally {
        notifying = false;
      }
    },
    stats() {
      return { suppressed, observerErrors, invalid };
    },
  };
}

export interface ConfirmationBufferSnapshot {
  readonly records: readonly SaveConfirmation[];
  /** 상한을 넘어 버린 가장 오래된 기록 수. */
  readonly dropped: number;
}

export interface ConfirmationBuffer {
  readonly sink: ConfirmationSink;
  snapshot(): ConfirmationBufferSnapshot;
  /**
   * 앞에서부터 최대 `max`건을 **꺼내지 않고** 본다 (REV-005).
   *
   * 전송 대기열은 이 목록으로 batch를 계획한다. 계획 과정에서 크기 상한 때문에
   * 일부가 빠질 수 있으므로, 여기서 버퍼를 비우면 빠진 기록이 갈 곳을 잃는다.
   */
  peek(max: number): readonly SaveConfirmation[];
  /**
   * 실어 보낸 기록을 버퍼에서 뺀다. 실제로 뺀 수를 돌려준다 (REV-005).
   *
   * 서버 ACK를 받은 뒤에만 부른다. 그 전에 빼면 재시도·새로고침·영구 실패에서
   * 확인 기록이 조용히 사라진다. **수가 아니라 `peek`이 준 그 기록**으로 뺀다 ——
   * ACK를 기다리는 동안 상한을 넘어 앞이 밀리면, 앞에서부터 수만큼 빼는 방식은
   * 아직 보내지 않은 기록을 대신 지운다.
   */
  commit(sent: readonly SaveConfirmation[]): number;
  /** 아직 서버 ACK를 받지 못해 버퍼에 남아 있는 기록 수. */
  size(): number;
}

export const DEFAULT_CONFIRMATION_CAPACITY = 500;

/**
 * 메모리에만 두는 확인 기록 버퍼. **저장소에 쓰지 않는다.**
 *
 * 브라우저 저장소에 적으면 그 쓰기를 다시 확인해야 하고, 확인 기록이 확인
 * 기록을 낳는다. 상한을 넘으면 오래된 것부터 버리고 버린 수를 센다.
 */
export function createConfirmationBuffer(capacity: number = DEFAULT_CONFIRMATION_CAPACITY): ConfirmationBuffer {
  if (!Number.isSafeInteger(capacity) || capacity < 1) {
    throw new Error('확인 기록 버퍼 상한은 1 이상의 정수여야 합니다.');
  }
  let records: SaveConfirmation[] = [];
  let dropped = 0;
  return {
    sink(record) {
      records.push(record);
      if (records.length > capacity) {
        const overflow = records.length - capacity;
        records = records.slice(overflow);
        dropped += overflow;
      }
    },
    snapshot() {
      return { records: Object.freeze([...records]), dropped };
    },
    peek(max) {
      const count = Math.max(0, Math.min(Math.floor(max), records.length));
      return Object.freeze(records.slice(0, count));
    },
    commit(sent) {
      const targets = new Set(sent);
      const before = records.length;
      records = records.filter((record) => !targets.has(record));
      return before - records.length;
    },
    size() {
      return records.length;
    },
  };
}
